/**
 * Tenant-configurable application fonts.
 *
 * The selected font key is stored per-tenant on the backend (Brand Settings ->
 * `BRAND_FONT_FAMILY`) and delivered at bootstrap via
 * `environment.runtime.BRAND_FONT_FAMILY`. The key is resolved here to a CSS
 * font stack and applied to the `--app-font-family` custom property, which the
 * whole UI (and charts, via styles.constants) reads.
 *
 * To add a font: append an entry here and a matching option on the backend
 * `BRAND_FONT_FAMILY` select. Self-hosted fonts also need an `@font-face`
 * declaration in `styles.scss` and the font files under
 * `src/assets/vendors/base/fonts/`.
 */

export interface AppFontDefinition {
	/** Label shown in the Brand Settings dropdown (kept in sync with backend). */
	label: string;
	/** Full CSS font stack. Always ends in the default fallback chain. */
	stack: string;
	/** True when the font is bundled via @font-face under assets/vendors/base/fonts/. */
	selfHosted: boolean;
}

/** Default fallback stack, used when no/unknown font is configured. */
export const DEFAULT_FONT_STACK = 'Inter, "Helvetica Neue", sans-serif';

/** Registry of supported fonts, keyed by the value stored in BRAND_FONT_FAMILY. */
export const APP_FONTS: Record<string, AppFontDefinition> = {
	default: {
		label: 'System Default (Inter)',
		stack: DEFAULT_FONT_STACK,
		selfHosted: false
	},
	dubai: {
		label: 'Dubai',
		// Quote multi-word / self-hosted family names with DOUBLE quotes, matching
		// DEFAULT_FONT_STACK. The stack is embedded verbatim into single-quoted SVG
		// attributes (e.g. fleet-dashboard map-marker icons: `font-family='…'`), so a
		// single-quoted name would close the attribute and break the marker icon.
		stack: `"Dubai", ${DEFAULT_FONT_STACK}`,
		selfHosted: true
	}
};

export const DEFAULT_FONT_KEY = 'default';

/** CSS custom property that drives typography across the application. */
export const APP_FONT_CSS_VAR = '--app-font-family';

/**
 * Resolve a configured font key to its CSS stack. Unknown / empty keys fall
 * back to the default stack (case-insensitive so "Dubai" === "dubai").
 */
export function resolveFontStack(fontKey: string | null | undefined): string {
	const key = (fontKey || '').toString().trim().toLowerCase();
	return APP_FONTS[key]?.stack ?? DEFAULT_FONT_STACK;
}

/**
 * Apply the configured tenant font by setting the `--app-font-family` custom
 * property on the document root. DI-free so it can run pre-bootstrap (main.ts)
 * for flash-free typography. Safe to call in non-browser contexts (no-ops).
 */
export function applyAppFont(fontKey: string | null | undefined): void {
	if (typeof document === 'undefined' || !document.documentElement) {
		return;
	}
	document.documentElement.style.setProperty(APP_FONT_CSS_VAR, resolveFontStack(fontKey));
}
